import { fromByteArray } from 'base64-js';
import { XMLBuilder } from 'fast-xml-parser';
import { ulid } from 'ulid';

import { AbortEndpointRequestError } from '@iconik-io/iconik-api/src/client/response';

import {
  AbortUploadError,
  InvalidFileObject,
  MultipartUploadChunkError,
  UninitializedChunkError,
} from '../../errors';
import {
  UPLOADER_CHUNK_STATUS_COMPLETED,
  UPLOADER_CHUNK_STATUS_FAILED,
  UPLOADER_CHUNK_STATUS_UPLOADING,
  UPLOADER_STATUS_COMPLETING,
} from '../../state';
import type { UploadEntity } from '../../types';
import { MultipartUploader } from '../multipart_uploader';
import { AzureInvalidSignatureParamsError } from './errors';

const MS_VERSION = '2021-06-08';

const xmlBuilder = new XMLBuilder({
  ignoreAttributes: false,
  format: true,
});

const xmlDeclaration = {
  '?xml': {
    '@_version': '1.0',
    '@_encoding': 'UTF-8',
    '@_standalone': 'yes',
  },
};

const requiredAzureSignatureParams = [
  'st',
  'se',
  'sp',
  'sv',
  'sr',
  'sig',
] as const;

type AzureSignatureParams = {
  [param in (typeof requiredAzureSignatureParams)[number]]: string;
};

type AzureMeta = {
  uploadUrl: string;
};

type AzureChunkMeta = {
  chunkId: string;
};

export class AzureUploader extends MultipartUploader<
  AzureMeta,
  AzureChunkMeta
> {
  #azureSignatureParams: AzureSignatureParams | undefined;

  protected validate(): boolean {
    return true;
  }

  handleUploadEntityCreated({ entity }: { entity: UploadEntity }) {
    if (!entity.upload_url) {
      throw new InvalidFileObject(entity, 'Empty upload URL');
    }
    this.updateState({
      meta: {
        uploadUrl: entity.upload_url,
      },
    });
  }

  init() {
    this.on('uploadEntityCreated', this.handleUploadEntityCreated);
    super.init();
  }

  #assertSignatureParams(
    params: Record<string, string>,
  ): asserts params is AzureSignatureParams {
    for (const key of requiredAzureSignatureParams) {
      if (!params[key]) {
        throw new AzureInvalidSignatureParamsError(
          `Signature param ${key} is missing`,
        );
      }
    }
  }

  #generateChunkId(chunkIndex: number): string {
    const id = `${ulid()}-${String(chunkIndex).padStart(6, '0')}`;
    const textEncoder = new TextEncoder();
    return fromByteArray(textEncoder.encode(id));
  }

  async #uploadChunkIndex(chunkIndex: number): Promise<void> {
    const chunk = this.unwrapChunks()[chunkIndex];
    if (!chunk) {
      throw new UninitializedChunkError(chunkIndex);
    }

    this.updateChunks([
      { index: chunkIndex, status: UPLOADER_CHUNK_STATUS_UPLOADING },
    ]);

    const body = await this.readChunkData(
      chunkIndex,
      this.abortList.newSignal(),
    );

    const chunkId = this.#generateChunkId(chunkIndex);
    this.updateChunks([{ index: chunkIndex, meta: { chunkId } }]);

    return this.httpClient
      .request(
        {
          method: 'put',
          url: this.unwrapMeta().uploadUrl,
          headers: {
            'x-ms-version': MS_VERSION,
          },
          body,
          query: {
            ...this.#azureSignatureParams,
            comp: 'block',
            // cspell:ignore blockid
            blockid: chunkId,
          },
        },
        {
          abortSignal: this.abortList.newSignal(),
          onUploadProgress: (event) =>
            this.uploadChunkProgress(chunkIndex, event),
        },
      )
      .then(() => {
        this.updateChunks([
          {
            index: chunkIndex,
            status: UPLOADER_CHUNK_STATUS_COMPLETED,
          },
        ]);
      })
      .catch((error) => {
        if (
          error instanceof AbortUploadError ||
          error instanceof AbortEndpointRequestError
        ) {
          throw error;
        }
        this.updateChunks([
          { index: chunkIndex, status: UPLOADER_CHUNK_STATUS_FAILED },
        ]);
        throw new MultipartUploadChunkError(chunk, error);
      });
  }

  checkIfUploadExpired(_assetId: string, _fileId: string): Promise<boolean> {
    return Promise.resolve(false);
  }

  protected async runUpload(): Promise<void> {
    const uploadUrl = this.unwrapMeta().uploadUrl;
    const azureSignatureParams = Object.fromEntries(
      new URL(uploadUrl).searchParams.entries(),
    );
    this.#assertSignatureParams(azureSignatureParams);
    this.#azureSignatureParams = azureSignatureParams;

    await this.chunksQueue.addAll(
      this.getIncompleteChunks().map(
        (chunk) => () => this.#uploadChunkIndex(chunk.index),
      ),
    );

    await this.#complete();
  }

  #complete(): Promise<unknown> {
    this.updateState({ status: UPLOADER_STATUS_COMPLETING });

    const xmlObject = {
      ...xmlDeclaration,
      BlockList: {
        Latest: this.unwrapChunks().map(
          (chunk) => this.unwrapChunkMeta(chunk.index).chunkId,
        ),
      },
    };

    const body = xmlBuilder.build(xmlObject);

    return this.httpClient.request(
      {
        method: 'put',
        url: this.unwrapMeta().uploadUrl,
        headers: {
          'x-ms-version': MS_VERSION,
        },
        body,
        query: {
          ...this.#azureSignatureParams,
          comp: 'blocklist',
        },
      },
      { abortSignal: this.abortList.newSignal() },
    );
  }

  protected async runCancel(): Promise<void> {}
}
